import React, { useState } from "react";
import emailjs from "@emailjs/browser";

const services = [
  {
    title: "Corporate Website Design",
    desc: "We create responsive websites that drive results and reflect your brand's unique vision.",
    icon: "/assets/services/web.webp",
    details:
      "Our corporate website design service focuses on creating modern, responsive websites that align with your business goals. We combine aesthetic appeal with functionality to ensure your website not only looks great but also performs exceptionally well across all devices.",
  },
  {
    title: "Mobile App Development",
    desc: "We deliver high-performing apps with user-focused design and broad platform compatibility.",
    icon: "/assets/services/mobile.webp",
    details:
      "From concept to launch, we develop native and cross-platform mobile applications. Our team specializes in iOS, Android, and hybrid app development, ensuring seamless performance and engaging user experiences.",
  },
  {
    title: "Software Development",
    desc: "Custom software that simplifies processes, lowers expenses, and supports business scalability.",
    icon: "/assets/services/software.webp",
    details:
      "We build custom software solutions tailored to your specific business needs. Our development process includes requirements analysis, agile development, testing, and ongoing maintenance.",
  },
  {
    title: "Logo Design",
    desc: "User-focused designs enhance navigation, engagement, and customer satisfaction.",
    icon: "/assets/services/logo.webp",
    details:
      "Our logo design service creates memorable brand identities. We work closely with you to understand your brand values and target audience, delivering logos that communicate your unique story.",
  },
  {
    title: "Salesforce",
    desc: "Data migration involves moving data from one system to another, with preparation and cleanup.",
    icon: "/assets/services/salesforce.webp",
    details:
      "We provide comprehensive Salesforce implementation, customization, and integration services. Our experts help you leverage Salesforce CRM to streamline operations and improve customer relationships.",
  },
  {
    title: "E-Commerce Solutions",
    desc: "Our services go beyond expectations, offering high quality and round-the-clock support.",
    icon: "/assets/services/ecommerce.webp",
    details:
      "We develop robust e-commerce platforms with secure payment gateways, inventory management, and responsive designs. Our solutions are scalable to grow with your business.",
  },
  {
    title: "Company Profile Design",
    desc: "Visually engaging profiles that reflect professionalism and strengthen confidence.",
    icon: "/assets/services/profile.webp",
    details:
      "We design professional company profiles and corporate brochures that effectively communicate your brand story, services, and achievements to stakeholders and potential clients.",
  },
  {
    title: "UI/UX Design",
    desc: "User-focused designs enhance navigation, engagement, and customer satisfaction.",
    icon: "/assets/services/uiux.webp",
    details:
      "Our UI/UX design service focuses on creating intuitive and engaging user interfaces. We conduct user research, create wireframes, and develop prototypes to ensure optimal user experiences.",
  },
  {
    title: "SAP Migration and Implementation",
    desc: "Our services go beyond expectations, offering high quality and round-the-clock support.",
    icon: "/assets/services/sap.webp",
    details:
      "We provide end-to-end SAP implementation and migration services, ensuring smooth transition with minimal disruption to your business operations. Our team handles configuration, customization, and training.",
  },
];

const ServicesGridSection = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const openModal = (service) => {
    setSelectedService(service);
    setIsModalOpen(true);
    setShowContactForm(false);
    setSubmitSuccess(false);
    setFormData({
      name: "",
      email: "",
      phone: "",
      company: "",
      message: "",
    });
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedService(null);
    setShowContactForm(false);
    setSubmitSuccess(false);
    document.body.style.overflow = "unset";
  };

  const handleGetStarted = () => {
    setShowContactForm(true);
    setSubmitSuccess(false);
  };

  const handleBackToService = () => {
    setShowContactForm(false);
    setSubmitSuccess(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          service: selectedService.title,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          message: formData.message,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
      );

      setSubmitSuccess(true);
    } catch (error) {
      console.error("Email error:", error);
      alert("Failed to send email.");
    }

    setIsSubmitting(false);
  };

  return (
    <>
      <section className="w-full bg-[#0b4864] py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((item, index) => (
              <div
                key={index}
                className="
                  bg-white
                  rounded-xl
                  shadow-[0_8px_24px_rgba(0,0,0,0.2)]
                  px-6 py-8
                  text-center
                  transition
                  hover:-translate-y-1
                  hover:shadow-[0_12px_30px_rgba(0,0,0,0.25)]
                "
              >
                {/* Icon */}
                <div className="flex justify-center mb-4">
                  <div className="h-14 w-14 rounded-full bg-white shadow flex items-center justify-center">
                    <img src={item.icon} alt={item.title} className="h-7 w-7" />
                  </div>
                </div>

                {/* Title */}
                <h3 className="font-semibold text-sm sm:text-base text-gray-900">
                  {item.title}
                </h3>

                {/* Description */}
                <p className="mt-3 text-sm text-gray-600 leading-6">
                  {item.desc}
                </p>

                {/* CTA */}
                <div className="mt-5">
                  <button
                    onClick={() => openModal(item)}
                    className="
                      bg-[#0b4864]
                      text-white
                      text-sm
                      px-5 py-2
                      rounded-md
                      transition
                      hover:bg-[#093b52]
                    "
                  >
                    Click here
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modal Overlay */}
      {isModalOpen && selectedService && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={closeModal}
          />

          {/* Modal Container */}
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div
              className="
                relative
                bg-white
                rounded-2xl
                shadow-2xl
                w-full
                max-w-2xl
                mx-auto
                overflow-hidden
                transform
                transition-all
                animate-fadeIn
              "
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-[#0b4864] px-6 py-4 sm:px-8 sm:py-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="h-12 w-12 rounded-full bg-white shadow flex items-center justify-center">
                      <img
                        src={selectedService.icon}
                        alt={selectedService.title}
                        className="h-6 w-6"
                      />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-white">
                      {showContactForm ? "Contact Us" : selectedService.title}
                    </h3>
                  </div>
                  <button
                    onClick={closeModal}
                    className="
                      text-white
                      hover:text-gray-200
                      text-2xl
                      font-light
                      leading-none
                      transition
                    "
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="px-6 py-6 sm:px-8 sm:py-8">
                {!showContactForm ? (
                  // Service Details View
                  <>
                    <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
                      {selectedService.details}
                    </p>

                    {/* Additional Features/Highlights */}
                    <div className="mt-8 pt-6 border-t border-gray-200">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">
                        Key Features
                      </h4>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <li className="flex items-center text-gray-600">
                          <span className="w-2 h-2 bg-[#0b4864] rounded-full mr-3"></span>
                          Professional Team
                        </li>
                        <li className="flex items-center text-gray-600">
                          <span className="w-2 h-2 bg-[#0b4864] rounded-full mr-3"></span>
                          24/7 Support
                        </li>
                        <li className="flex items-center text-gray-600">
                          <span className="w-2 h-2 bg-[#0b4864] rounded-full mr-3"></span>
                          Quality Assurance
                        </li>
                        <li className="flex items-center text-gray-600">
                          <span className="w-2 h-2 bg-[#0b4864] rounded-full mr-3"></span>
                          Timely Delivery
                        </li>
                      </ul>
                    </div>

                    {/* Action Buttons */}
                    <div className="mt-8 flex flex-col sm:flex-row gap-4">
                      <button
                        onClick={handleGetStarted}
                        className="
                          flex-1
                          bg-[#0b4864]
                          text-white
                          font-medium
                          py-3
                          px-6
                          rounded-lg
                          transition
                          hover:bg-[#093b52]
                          transform
                          hover:-translate-y-0.5
                        "
                      >
                        Get Started
                      </button>
                      <button
                        onClick={closeModal}
                        className="
                          flex-1
                          border-2
                          border-gray-300
                          text-gray-700
                          font-medium
                          py-3
                          px-6
                          rounded-lg
                          transition
                          hover:border-gray-400
                          hover:bg-gray-50
                        "
                      >
                        Learn More
                      </button>
                    </div>
                  </>
                ) : !submitSuccess ? (
                  // Contact Form View
                  <>
                    <div className="mb-6">
                      <p className="text-gray-600">
                        Interested in our{" "}
                        <span className="font-semibold text-[#0b4864]">
                          {selectedService.title}
                        </span>{" "}
                        service? Fill out the form below and we'll get back to
                        you within 24 hours.
                      </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Full Name *
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            className="
                              w-full
                              px-4
                              py-3
                              border
                              border-gray-300
                              rounded-lg
                              focus:ring-2
                              focus:ring-[#0b4864]
                              focus:border-transparent
                              outline-none
                              transition
                            "
                            placeholder="John Doe"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email Address *
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            className="
                              w-full
                              px-4
                              py-3
                              border
                              border-gray-300
                              rounded-lg
                              focus:ring-2
                              focus:ring-[#0b4864]
                              focus:border-transparent
                              outline-none
                              transition
                            "
                            placeholder="john@example.com"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="
                              w-full
                              px-4
                              py-3
                              border
                              border-gray-300
                              rounded-lg
                              focus:ring-2
                              focus:ring-[#0b4864]
                              focus:border-transparent
                              outline-none
                              transition
                            "
                            placeholder="+1 (555) 123-4567"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Company Name
                          </label>
                          <input
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleInputChange}
                            className="
                              w-full
                              px-4
                              py-3
                              border
                              border-gray-300
                              rounded-lg
                              focus:ring-2
                              focus:ring-[#0b4864]
                              focus:border-transparent
                              outline-none
                              transition
                            "
                            placeholder="Your Company Inc."
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Project Details / Message *
                        </label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          required
                          rows="4"
                          className="
                            w-full
                            px-4
                            py-3
                            border
                            border-gray-300
                            rounded-lg
                            focus:ring-2
                            focus:ring-[#0b4864]
                            focus:border-transparent
                            outline-none
                            transition
                          "
                          placeholder={`Tell us about your ${selectedService.title} project requirements...`}
                        />
                      </div>

                      <div className="flex flex-col sm:flex-row gap-4 pt-4">
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="
                            flex-1
                            bg-[#0b4864]
                            text-white
                            font-medium
                            py-3
                            px-6
                            rounded-lg
                            transition
                            hover:bg-[#093b52]
                            transform
                            hover:-translate-y-0.5
                            disabled:opacity-50
                            disabled:cursor-not-allowed
                            disabled:transform-none
                          "
                        >
                          {isSubmitting ? (
                            <span className="flex items-center justify-center">
                              <svg
                                className="animate-spin h-5 w-5 mr-3 text-white"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                  fill="none"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                              </svg>
                              Submitting...
                            </span>
                          ) : (
                            "Submit Request"
                          )}
                        </button>

                        <button
                          type="button"
                          onClick={handleBackToService}
                          className="
                            flex-1
                            border-2
                            border-gray-300
                            text-gray-700
                            font-medium
                            py-3
                            px-6
                            rounded-lg
                            transition
                            hover:border-gray-400
                            hover:bg-gray-50
                          "
                        >
                          Back to Service
                        </button>
                      </div>
                    </form>
                  </>
                ) : (
                  // Success Message View
                  <div className="text-center py-8">
                    <div className="mb-6">
                      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg
                          className="w-10 h-10 text-green-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        Thank You!
                      </h3>
                      <p className="text-gray-600">
                        Your request for{" "}
                        <span className="font-semibold text-[#0b4864]">
                          {selectedService.title}
                        </span>{" "}
                        has been submitted successfully.
                      </p>
                      <p className="text-gray-600 mt-2">
                        Our team will contact you within 24 hours to discuss
                        your project.
                      </p>
                    </div>

                    <button
                      onClick={closeModal}
                      className="
                        bg-[#0b4864]
                        text-white
                        font-medium
                        py-3
                        px-8
                        rounded-lg
                        transition
                        hover:bg-[#093b52]
                        transform
                        hover:-translate-y-0.5
                      "
                    >
                      Close
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default ServicesGridSection;

// import React from "react";

// const services = [
//   {
//     title: "Corporate Website Design",
//     desc: "We create responsive websites that drive results and reflect your brand’s unique vision.",
//     icon: "/assets/services/web.webp",
//   },
//   {
//     title: "Mobile App Development",
//     desc: "We deliver high-performing apps with user-focused design and broad platform compatibility.",
//     icon: "/assets/services/mobile.webp",
//   },
//   {
//     title: "Software Development",
//     desc: "Custom software that simplifies processes, lowers expenses, and supports business scalability.",
//     icon: "/assets/services/software.webp",
//   },
//   {
//     title: "Logo Design",
//     desc: "User-focused designs enhance navigation, engagement, and customer satisfaction.",
//     icon: "/assets/services/logo.webp",
//   },
//   {
//     title: "Salesforce",
//     desc: "Data migration involves moving data from one system to another, with preparation and cleanup.",
//     icon: "/assets/services/salesforce.webp",
//   },
//   {
//     title: "E-Commerce Solutions",
//     desc: "Our services go beyond expectations, offering high quality and round-the-clock support.",
//     icon: "/assets/services/ecommerce.webp",
//   },
//   {
//     title: "Company Profile Design",
//     desc: "Visually engaging profiles that reflect professionalism and strengthen confidence.",
//     icon: "/assets/services/profile.webp",
//   },
//   {
//     title: "UI/UX Design",
//     desc: "User-focused designs enhance navigation, engagement, and customer satisfaction.",
//     icon: "/assets/services/uiux.webp",
//   },
//   {
//     title: "SAP Migration and Implementation",
//     desc: "Our services go beyond expectations, offering high quality and round-the-clock support.",
//     icon: "/assets/services/sap.webp",
//   },
// ];

// const ServicesGridSection = () => {
//   return (
//     <section className="w-full bg-[#0b4864] py-14 sm:py-20">
//       <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
//         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
//           {services.map((item, index) => (
//             <div
//               key={index}
//               className="
//                 bg-white
//                 rounded-xl
//                 shadow-[0_8px_24px_rgba(0,0,0,0.2)]
//                 px-6 py-8
//                 text-center
//                 transition
//                 hover:-translate-y-1
//                 hover:shadow-[0_12px_30px_rgba(0,0,0,0.25)]
//               "
//             >
//               {/* Icon */}
//               <div className="flex justify-center mb-4">
//                 <div className="h-14 w-14 rounded-full bg-white shadow flex items-center justify-center">
//                   <img src={item.icon} alt={item.title} className="h-7 w-7" />
//                 </div>
//               </div>

//               {/* Title */}
//               <h3 className="font-semibold text-sm sm:text-base text-gray-900">
//                 {item.title}
//               </h3>

//               {/* Description */}
//               <p className="mt-3 text-sm text-gray-600 leading-6">
//                 {item.desc}
//               </p>

//               {/* CTA */}
//               <div className="mt-5">
//                 <button
//                   className="
//                     bg-[#0b4864]
//                     text-white
//                     text-sm
//                     px-5 py-2
//                     rounded-md
//                     transition
//                     hover:bg-[#093b52]
//                   "
//                 >
//                   Click here
//                 </button>
//               </div>
//             </div>
//           ))}
//         </div>
//       </div>
//     </section>
//   );
// };

// export default ServicesGridSection;
